import dayjs from "dayjs";
import {
  CYCLE_UNIT_DAILY,
  CYCLE_UNIT_MONTHLY,
  CYCLE_UNIT_WEEKLY,
  CYCLE_UNIT_YEARLY
} from "../constants/cycleUnit";
import {
  PROPORTIONAL_METHOD_DAYS_PER_MONTH_30,
  PROPORTIONAL_METHOD_DAYS_PER_MONTH_ACTUAL,
  PROPORTIONAL_METHOD_DAYS_PER_YEAR,
  PROPORTIONAL_METHOD_FREE
} from "../constants/proportionalCalculationMethod";
import { PAYMENT_METHOD_SEPA } from "./paymentMethodCodes";

const CENTS_PER_UNIT = 100;
const MONTHS_PER_YEAR = 12;
const WEEKS_PER_YEAR = 52;
const DAYS_PER_COMMON_YEAR = 365;
const DAYS_PER_LEAP_YEAR = 366;
const GREGORIAN_LEAP_CYCLE_YEARS = 400;
const LEAP_YEAR_STEP_YEARS = 4;
const LEAP_YEAR_CENTURY_YEARS = 100;
/** Monat mit festen 30 Tagen (Methode DaysPerMonth30). */
const FIXED_DAYS_PER_MONTH = 30;

/**
 * Rundet eine Zahl auf 2 Dezimalstellen, weg von Null (0.5 -> 1, -0.5 -> -1)
 */
export function round2AwayFromZero(value: number): number {
  const v = Number(value || 0);
  const sign = v < 0 ? -1 : 1;
  return sign * Math.round(Math.abs(v) * CENTS_PER_UNIT) / CENTS_PER_UNIT;
}

/**
 * Vorschau des anteiligen Beitrags für die Lücke zwischen Trainingsbeginn und Vertragsbeginn.
 *
 * **Anzeige-Kopie, nicht maßgeblich.** Der abgerechnete Betrag wird im Backend berechnet
 * (`ProportionalCalculationService.CalculateGapProportionalValue`) und darf nicht von hier
 * übernommen werden. Diese Funktion existiert nur, damit der Betrag im Formular sichtbar ist,
 * bevor gespeichert wird.
 *
 * Das Ergebnis darf **nicht** in `proportionalValueOverride` geschrieben werden: dieses Feld
 * bedeutet „ein Mensch hat den Betrag von Hand bestimmt". Wird es automatisch befüllt, kann das
 * Backend beides nicht mehr auseinanderhalten.
 *
 * `contractCreation.proportionalPreview.test.ts` hält die Vorschau gegen dieselben Fälle wie
 * `ProportionalGapCalculationTests.cs` im Backend — läuft die Kopie auseinander, wird der Test rot.
 */
export function calculateProportionalPreview(
  baseValue: number,
  trainingStartIso: string,
  contractStartIso: string,
  method: number,
  paymentCycleUnit: number | null | undefined
): { prorated: number; ratio: number | null } {
  const base = Number(baseValue || 0);
  const m = Number(method || 0);
  const start = dayjs(trainingStartIso).startOf("day");
  const end = dayjs(contractStartIso).startOf("day");
  if (!start.isValid() || !end.isValid() || !m) return { prorated: base, ratio: null };
  if (m === PROPORTIONAL_METHOD_FREE) return { prorated: 0, ratio: 0 };

  const daysBetween = Math.max(0, end.diff(start, "day"));
  if (daysBetween <= 0) return { prorated: 0, ratio: 0 };

  const cu = paymentCycleUnit == null ? 1 : Number(paymentCycleUnit); // default monthly
  const toYearFactor = () => {
    if (cu === CYCLE_UNIT_YEARLY) return 1; // yearly
    if (cu === CYCLE_UNIT_MONTHLY) return MONTHS_PER_YEAR; // monthly
    if (cu === CYCLE_UNIT_WEEKLY) return WEEKS_PER_YEAR; // weekly
    if (cu === CYCLE_UNIT_DAILY) return DAYS_PER_COMMON_YEAR; // daily
    return 1;
  };
  const toMonthFactor = () => {
    if (cu === CYCLE_UNIT_YEARLY) return 1 / MONTHS_PER_YEAR; // yearly -> monthly
    if (cu === CYCLE_UNIT_MONTHLY) return 1; // monthly
    if (cu === CYCLE_UNIT_WEEKLY) return WEEKS_PER_YEAR / MONTHS_PER_YEAR; // weekly -> approx month
    if (cu === CYCLE_UNIT_DAILY) return DAYS_PER_COMMON_YEAR / MONTHS_PER_YEAR; // daily -> approx month
    return 1;
  };

  if (m === PROPORTIONAL_METHOD_DAYS_PER_YEAR) {
    // DaysPerYear (bezogen auf das Jahr des Trainingsbeginns)
    const year = start.year();
    const daysInYear = ((year % LEAP_YEAR_STEP_YEARS === 0 && year % LEAP_YEAR_CENTURY_YEARS !== 0) || year % GREGORIAN_LEAP_CYCLE_YEARS === 0) ? DAYS_PER_LEAP_YEAR : DAYS_PER_COMMON_YEAR;
    const yearEndExclusive = start.startOf("year").add(1, "year");
    const effectiveEnd = end.isBefore(yearEndExclusive, "day") ? end : yearEndExclusive;
    const days = Math.max(0, effectiveEnd.diff(start, "day"));
    const ratio = daysInYear > 0 ? toYearFactor() * (days / daysInYear) : null;
    return { prorated: round2AwayFromZero(base * (ratio ?? 0)), ratio };
  }
  if (m === PROPORTIONAL_METHOD_DAYS_PER_MONTH_30) {
    // DaysPerMonth30
    const daysInMonth = FIXED_DAYS_PER_MONTH;
    const monthEndExclusive = start.startOf("month").add(1, "month");
    const effectiveEnd = end.isBefore(monthEndExclusive, "day") ? end : monthEndExclusive;
    const days = Math.max(0, effectiveEnd.diff(start, "day"));
    const ratio = daysInMonth > 0 ? toMonthFactor() * (days / daysInMonth) : null;
    return { prorated: round2AwayFromZero(base * (ratio ?? 0)), ratio };
  }
  if (m === PROPORTIONAL_METHOD_DAYS_PER_MONTH_ACTUAL) {
    // DaysPerMonthActual
    const daysInMonth = start.daysInMonth();
    const monthEndExclusive = start.startOf("month").add(1, "month");
    const effectiveEnd = end.isBefore(monthEndExclusive, "day") ? end : monthEndExclusive;
    const days = Math.max(0, effectiveEnd.diff(start, "day"));
    const ratio = daysInMonth > 0 ? toMonthFactor() * (days / daysInMonth) : null;
    return { prorated: round2AwayFromZero(base * (ratio ?? 0)), ratio };
  }

  return { prorated: base, ratio: null };
}

/**
 * Typ für Contract Component Draft (aus Vertragspaket)
 * Formerly: ContractServiceProductDraft
 */
export type ContractServiceProductDraft = {
  contractTemplateId: string; // Formerly serviceProductId
  contractTemplateName: string; // Formerly serviceProductName
  // Legacy support (will be removed):
  serviceProductId?: string;
  serviceProductName?: string;
  value?: number | null;
  allowPriceOverrideOnCreate?: boolean;
  isPrimary?: boolean;
  isProportionalFeeBase?: boolean;
  proportionalCalculationMethod?: number | null;
  proportionalValueOverride?: number | null;
  allowProportionalValueOverrideOnCreate?: boolean;
  paymentCycleUnit?: number | null;
  paymentMethod?: number | null;
  isActive?: boolean;
};

/**
 * Typ für Wallet Product Draft (aus Vertragspaket)
 */
export type ContractWalletProductDraft = {
  walletProductId: string;
  walletProductName: string;
  walletProductType?: number; // 1=Punkte, 2=Wert (optional, wird aus WalletProduct ermittelt falls nicht vorhanden)
  initialBalance?: number | null;
  validTo?: string | null;
  accessScope?: number | null;
  allowedStudioIds?: string[];
  requiresNoArrears?: boolean;
  isActive?: boolean;
  note?: string | null;
};

/**
 * Wallet Product Info (z.B. aus API)
 */
export type WalletProductInfo = {
  id: string;
  type?: number; // 1=Punkte, 2=Wert
  [key: string]: unknown;
};

/**
 * Extrahiert den Wallet-Typ aus einem Wallet Draft.
 * 
 * @param draft - Wallet Draft
 * @param walletProducts - Optionale Liste von Wallet Products (falls Typ nicht im Draft vorhanden)
 * @returns Wallet-Typ (1=Punkte, 2=Wert, 0=unbekannt)
 */
export function getWalletType(
  draft: ContractWalletProductDraft,
  walletProducts?: WalletProductInfo[]
): number {
  // Zuerst prüfen, ob Typ direkt im Draft vorhanden ist
  if (draft.walletProductType != null) {
    return Number(draft.walletProductType);
  }
  
  // Falls nicht, aus walletProducts suchen
  if (walletProducts && draft.walletProductId) {
    const wp = walletProducts.find((p) => String(p.id) === String(draft.walletProductId));
    if (wp?.type != null) {
      return Number(wp.type);
    }
  }
  
  return 0; // Unbekannt
}

/**
 * Typ für vorbereiteten Service für Backend-API
 */
export type PreparedContractService = {
  contractTemplateId: string; // Formerly serviceProductId
  value: number | null;
  proportionalValueOverride: number | null;
  paymentMethod: number | null;
  isActive: boolean;
};

/**
 * Typ für vorbereitetes Wallet für Backend-API
 */
export type PreparedContractWallet = {
  walletProductId: string;
  initialBalance: number;
  validTo?: string | null;
  accessScope?: number | null;
  allowedStudioIds?: string[];
  requiresNoArrears?: boolean;
  isActive: boolean;
  note?: string | null;
};

/**
 * Prüft, ob ein isProportionalFeeBase-Block angezeigt werden soll
 * (nur wenn Vertragsbeginn != Trainingsbeginn, also days > 0)
 */
export function shouldShowProportionalFeeBaseBlock(
  service: ContractServiceProductDraft,
  trainingStartIso: string | null,
  startDateIso: string | null
): boolean {
  if (service.isProportionalFeeBase !== true) return true; // Nicht-proportionale Blöcke immer anzeigen
  
  if (!trainingStartIso || !startDateIso) return false;
  
  const a = dayjs(trainingStartIso).startOf("day");
  const b = dayjs(startDateIso).startOf("day");
  if (!a.isValid() || !b.isValid()) return false;
  
  const days = Math.max(0, b.diff(a, "day"));
  return days > 0; // Nur anzeigen wenn Vertragsbeginn != Trainingsbeginn
}

function serviceDraftId(service: ContractServiceProductDraft): string {
  return String(service.contractTemplateId || service.serviceProductId || "");
}

function serviceDraftPaymentMethod(
  service: ContractServiceProductDraft,
  servicePaymentMethod?: Record<string, number | null> | null
): number | null {
  const contractTemplateId = String(service.contractTemplateId || "");
  const serviceProductId = String(service.serviceProductId || "");
  const fromForm =
    (contractTemplateId && servicePaymentMethod && Object.prototype.hasOwnProperty.call(servicePaymentMethod, contractTemplateId)
      ? servicePaymentMethod[contractTemplateId]
      : undefined) ??
    (serviceProductId && servicePaymentMethod && Object.prototype.hasOwnProperty.call(servicePaymentMethod, serviceProductId)
      ? servicePaymentMethod[serviceProductId]
      : undefined);
  if (fromForm != null) return Number(fromForm);
  return service.paymentMethod != null ? Number(service.paymentMethod) : null;
}

export function contractDraftRequiresSepa(
  bundleServiceDraft: ContractServiceProductDraft[],
  servicePaymentMethod?: Record<string, number | null> | null,
  packagePaymentMethod?: number | null
): boolean {
  if (packagePaymentMethod != null && Number(packagePaymentMethod) === PAYMENT_METHOD_SEPA) return true;
  return (bundleServiceDraft || [])
    .filter((service) => service.isActive !== false && serviceDraftId(service))
    .some((service) => serviceDraftPaymentMethod(service, servicePaymentMethod) === PAYMENT_METHOD_SEPA);
}

/**
 * Bereitet Services für die Vertragsanlage vor
 * 
 * @param bundleServiceDraft - Liste der Service Products aus dem Vertragspaket
 * @param allowOverrides - Ob Preisänderungen erlaubt sind
 * @param trainingStartIso - Trainingsbeginn (ISO Date String)
 * @param startDateIso - Vertragsbeginn (ISO Date String)
 * @returns Vorbereitete Services für Backend-API oder null wenn keine Overrides erlaubt
 */
export function prepareContractServices(
  bundleServiceDraft: ContractServiceProductDraft[],
  allowOverrides: boolean,
  trainingStartIso: string | null,
  startDateIso: string | null,
  servicePaymentMethod?: Record<string, number | null> | null
): PreparedContractService[] | null {
  if (!allowOverrides) return null;

  return bundleServiceDraft.filter((s) => s.isActive !== false).map((s) => {
    const priceEditable = s.allowPriceOverrideOnCreate !== false;
    const proratedEditable = s.allowProportionalValueOverrideOnCreate !== false;

    const isProportionalBlock = s.isProportionalFeeBase === true || (s.proportionalCalculationMethod != null && Number(s.proportionalCalculationMethod) > 0);

    return {
      contractTemplateId: s.contractTemplateId, // Formerly serviceProductId
      // We only pass a value override (if allowed).
      value: priceEditable && s.value != null ? Number(s.value) : null,
      // Nur ein **von Hand** bestimmter Betrag geht als Override ans Backend. Der anteilige Beitrag
      // selbst wird dort gerechnet (`ProportionalCalculationService.CalculateGapProportionalValue`);
      // `calculateProportionalPreview` ist reine Anzeige. Wurde hier der berechnete Wert
      // mitgeschickt, gewann er im Backend immer über die eigene Rechnung — die Rechnung im Backend
      // lief dann faktisch nie.
      proportionalValueOverride:
        isProportionalBlock && proratedEditable && s.proportionalValueOverride != null
          ? Number(s.proportionalValueOverride)
          : null,
      paymentMethod: serviceDraftPaymentMethod(s, servicePaymentMethod),
      isActive: true
    };
  });
}

/**
 * Bereitet Wallets für die Vertragsanlage vor
 * 
 * @param bundleWalletDraft - Liste der Wallet Products aus dem Vertragspaket
 * @returns Vorbereitete Wallets für Backend-API
 */
export function prepareContractWallets(
  bundleWalletDraft: ContractWalletProductDraft[]
): PreparedContractWallet[] {
  return bundleWalletDraft
    .filter((w) => w.walletProductId && w.isActive !== false)
    .map((w) => ({
      walletProductId: w.walletProductId,
      initialBalance: Number(w.initialBalance ?? 0),
      validTo: w.validTo ?? undefined,
      accessScope: w.accessScope ?? undefined,
      allowedStudioIds: w.allowedStudioIds ?? undefined,
      requiresNoArrears: w.requiresNoArrears ?? undefined,
      isActive: true,
      note: w.note ?? undefined
    }));
}
